import { CSSResultGroup, html, TemplateResult } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import baseStyles from '../../base/outline-element/outline-element.base.css.lit';
import componentStyles from './ghs-styled-text.css.lit';
import { OutlineStyledText } from '../../base/outline-styled-text/outline-styled-text';
import { SlotController } from '../../controllers/slot-controller';

export type TextVariant = 'default' | 'dark' | 'hero';
export type TextSizeVariant = 'small' | 'base';

/**
 * The GHS WYSIWYG component
 * @element ghs-wysiwyg
 * @slot - default slot supplied by outline-wysiwyg
 */
@customElement('ghs-styled-text')
export class GhsStyledText extends OutlineStyledText {
  static styles: CSSResultGroup = [
    baseStyles, // Styles from outline-element. Primarily to include @tailwind base;
    componentStyles, // Styles specific to ghs-styled-text.
  ];

  slots = new SlotController(this, true);

  @state() showingAll = false;
  @state() contentHeight: Number;

  /**
   * Styled text variant.
   */
  @property({ type: String, attribute: 'variant', reflect: true })
  variant: TextVariant = 'default';

  /**
   * Size text variant.
   */
  @property({ type: String, reflect: true })
  size: TextSizeVariant = 'base';

  /**
   * Height limit where show more is displayed.
   */
  @property() contentLimit: Number = 256;

  /**
   * Show more.
   */
  @property({ type: Boolean, reflect: true, attribute: 'show-more' })
  showMore = false;

  firstUpdated(): void {
    super.firstUpdated();
    // This timeout is important for slot controller to finish before these things run.
    setTimeout(() => {
      this.contentHeight =
        this.shadowRoot?.querySelector('.content')?.getBoundingClientRect()
          .height || 0;
    }, 0);
  }

  showMoreToggle() {
    this.showingAll = !this.showingAll;
  }

  renderShowMore(): TemplateResult {
    return html`
      <div class="styled-text__actions">
        ${!this.showingAll
          ? html`<button
              class="styled-text__trigger"
              @click=${this.showMoreToggle}
            >
              <ghs-icon
                name="add"
                library="regular"
                size="24px"
                mobile-size="24px"
              ></ghs-icon>
              Show more
            </button>`
          : html`<button
              class="styled-text__trigger"
              @click=${this.showMoreToggle}
            >
              <ghs-icon
                name="less"
                library="regular"
                size="24px"
                mobile-size="24px"
              ></ghs-icon>
              Show less
            </button>`}
      </div>
    `;
  }

  render(): TemplateResult {
    const classes = {
      'styled-text': true,
      [`styled-text--${this.variant}`]: true,
      [`styled-text--${this.size}`]: true,
      [`site--${this.site}`]: true,
      [`styled-text--show-more`]:
        this.showMore && this.contentHeight > this.contentLimit,
      [`styled-text--showing-all`]: this.showingAll,
    };

    return html`
      <div class="${classMap(classes)}">
        <div class="content">
          <slot></slot>
        </div>
        ${this.showMore && this.contentHeight > this.contentLimit
          ? this.renderShowMore()
          : null}
      </div>
    `;
  }
}
